import { mapState, mapGetters } from 'vuex'
import {
  dynamicMapActions,
  dynamicMapState,
  dynamicMapGetters
} from '@/common/util/store'

import Events from '@/configuration/Events'
import { fieldNameAsClassName } from '@/common/util/fieldName'
import ErrorsConf from '@/configuration/sources/ErrorsConfiguration.yml'
import fieldTypes from '@/configuration/sources/FieldTypes.yml'

/** COMPONENTS */
import KryptonLabel from '@/host/components/controls/KryptonLabel'

/** MIXINS */
import ConfigurationMixin from '@/host/components/mixins/Configuration'
import { VisibilityMixin } from '@/host/components/mixins/Visibility'
import RegexFields from '@/configuration/sources/RegexFields.yml'
import { FlashWarningSubscriberMixin } from '@/host/components/mixins/FlashWarningSubscriber'

export const FieldMixin = {
  components: { KryptonLabel },
  mixins: [ConfigurationMixin, VisibilityMixin, FlashWarningSubscriberMixin],
  inject: ['context'],
  inheritAttrs: false,
  data() {
    return {
      inputElement: null,
      hasFocus: false,
      hasKeyboardFocus: false,
      mouseDownTriggered: false,
      domFieldName: '',
      wrapperClassName: ''
    }
  },
  computed: {
    ...mapState(['language']),
    ...mapGetters([
      'translate',
      'isSinglePaymentButton',
      'effectivePaymentMethodList'
    ]),
    ...mapState({
      formPlaceholder(state) {
        return state.form.placeholders[this.fieldName]
      },
      dnaConfiguration(state) {
        const smartFormFields =
          state.dna?.smartForm?.[this.selectedBrand]?.fields || {}
        // separated form since KJS-4787
        if (smartFormFields[this.fieldName]) {
          return smartFormFields[this.fieldName]
        }
        return state.dna?.cards[this.selectedBrand]?.fields[this.fieldName]
      }
    }),
    ...dynamicMapGetters('context.namespace', [
      'checkFieldCondition',
      'isFieldViable',
      'lastVisibleField'
    ]),
    ...dynamicMapState('context.namespace', ['selectedBrand']),
    dynamicClasses() {
      return {
        element: {
          [`kr-${this.domFieldName}`]: true,
          'kr-error': this.isInvalid,
          'kr-disabled': this.isDisabled,
          'kr-focus': this.hasFocus,
          'kr-keyboard-focus': this.hasKeyboardFocus,
          'kr-read-only': this.isReadOnly,
          'kr-field-element--warning': this.applyWarningStyle,
          'kr-field-element--last-visible':
            this.lastVisibleField === this.fieldName &&
            this.isSinglePaymentButton &&
            this.effectivePaymentMethodList?.length > 1
        }
      }
    },
    isRestrictedByDnaRule() {
      const enableIf = this?.dnaConfiguration?.enableIf
      if (!enableIf) return false

      return !this.checkFieldCondition(enableIf)
    },
    configuration() {
      const conf = this.dnaConfiguration

      if (this.isRestrictedByDnaRule && this.configurationWhenRestricted) {
        return this.configurationWhenRestricted
      }

      return conf
    },
    /**
     * Default declaration. Must be overloaded by specific field component.
     */
    configurationWhenRestricted() {
      return null
    },
    defaultLabel() {
      return this.translate(`label_${this.domFieldName.replace(/\-/g, '_')}`)
    },
    isIframeField() {
      return fieldTypes.iframe.includes(this.fieldName)
    },
    required() {
      return !!this.configuration?.required && this.isVisible
    }
  },
  created() {
    // Set data
    this.domFieldName = fieldNameAsClassName(this.fieldName)
    this.wrapperClassName = `kr-field-wrapper-${this.fieldName}`

    // Start subscriptions
    this.$busOn(Events.krypton.field.event, message => {
      const { formId, name, type, metadata = {} } = message

      if (formId === this.context.formId && name === this.fieldName) {
        switch (type) {
          case 'triggerFocusActions':
            // @see KJS-5076 - Metadata keyboard added in slave mixins
            // FocusReporter & Event to determine whether it's a keyboard focus
            this.mouseDownTriggered = metadata?.mouse === true
            this.onFocus()
            break
          case 'blur':
          case 'triggerBlurActions':
            this.onBlur()
            break
        }
      }
    })
    this.$busOn(
      Events.krypton.field.focus,
      async ({ formId, name, backwards }) => {
        if (formId !== this.context.formId) return
        if (name === this.fieldName && this.isReadOnly) {
          // wait for all field listeners to be executed to avoid inconsistency see KJS-3655
          await this.$nextTick()
          this.$bus.$emit(Events.krypton.field.event, {
            formId: this.context.formId,
            name,
            type: backwards ? 'reverseTab' : 'tab'
          })
        } else {
          this[name === this.fieldName ? 'onFocus' : 'onBlur']()
        }
      }
    )

    if (this.fieldType !== 'select') this.validate()
  },
  mounted() {
    this.setInput()
  },
  watch: {
    hasFocus(val, oldVal) {
      this.focusing(val, oldVal)
      /* for pan, the field is already invalidated after focus change in LuhnValidatorMixin
       not checking here avoids invalidating pan field on browser autofill.
       In some browsers the blur happens before the value update see KJS-5363
      */
      if (
        !val &&
        this.fieldName !== 'pan' &&
        !this.isFieldViable(this.fieldName)
      ) {
        this.invalidateField(this.fieldName)
      }
    },
    'configuration.hidden'(newVal, oldVal) {
      if (newVal !== oldVal) this.validate()
    }
  },
  methods: {
    ...dynamicMapActions('context.namespace', [
      'updateNonSensitiveValue',
      'viableField',
      'inviableField',
      'invalidateField',
      'setRegularFieldError',
      'resetRegularFieldError'
    ]),

    setInput() {},

    onKeyboardFocus() {
      if (!this.isReadOnly) this.hasKeyboardFocus = true
    },

    onMouseDown(evt) {
      this.mouseDownTriggered = true
    },

    onFocus() {
      if (!this.isReadOnly) {
        this.hasFocus = true
        this.hasKeyboardFocus = !this.mouseDownTriggered
      }
    },

    onBlur() {
      this.mouseDownTriggered = false

      if (!this.isReadOnly) {
        this.hasFocus = false
        this.hasKeyboardFocus = false
      }
    },

    focusing(hasFocus, hadFocus) {
      const type = hasFocus && !hadFocus ? 'focus' : 'blur'

      if (!this.isReadOnly && type) {
        if (this.inputElement) {
          this.inputElement[type]()
          const method =
            type === 'focus' ? 'addEventListener' : 'removeEventListener'
          this.inputElement[method]('keydown', this.focusEvents)
        }
        // KJS-5387 - event krypton.field.event already comes from iframe
        // We don't want to double the call
        if (this.isIframeField) return

        this.$bus.$emit(Events.krypton.field.event, {
          formId: this.context.formId,
          type: 'focus' === type ? 'triggerFocusActions' : 'blur',
          name: this.fieldName,
          metadata: {
            mouse: this.mouseDownTriggered
          }
        })
      }
    },

    focusEvents() {},

    validate() {
      this.resetRegularFieldError(this.fieldName)

      // @see KJS-6184 - defined in KryptonPocket component as
      // pocketType has a specific logic (must be always visible even if not
      // required for a card brand)
      if (this.fieldName === 'pocketType') return this.validatePocketType()

      if (!this.configuration || this.configuration.hidden) return
      const value =
        this.fieldType === 'select' ? this.selectedOption : this.fieldValue
      let isRequired =
        this.configuration?.required ||
        ~['identityDocumentType', 'identityDocumentNumber'].indexOf(
          this.fieldName
        )

      // Allow installment number = -1
      if (this.fieldName === 'installmentNumber' && value === '-1') {
        isRequired = false
      } else if (this.fieldType === 'text' && isRequired && !value.length) {
        this.inviableField(this.fieldName)
        this.setRegularFieldError({
          fieldName: this.fieldName,
          errorCode: ErrorsConf.fields.required[this.fieldName]
        })
        return
      }

      // Regex validation
      const regexValidator = RegexFields.validators[this.fieldName]
      if (regexValidator && value in regexValidator) {
        const regex = new RegExp(regexValidator[value])
        if (!regex.test(value)) {
          this.inviableField(this.fieldName)
          return
        }
      }

      // HACK: KJS-2096. Email validation
      if (this.fieldName === 'cardHolderMail' && this.isEmailInvalid()) {
        this.inviableField(this.fieldName)
        return
      }

      /**
       * Requested by MP team. If field is filled-in, length must be at least 2.
       * However if length is 0, it depends on whether field is required, which
       * is already checked above.
       *
       * @see KJS-4652
       *
       */
      if (this.fieldName === 'cardHolderName' && this.isHolderNameInvalid()) {
        this.inviableField(this.fieldName)
        this.setRegularFieldError({
          fieldName: this.fieldName,
          errorCode: ErrorsConf.fields.default[this.fieldName]
        })
        return
      }
      this[`${isRequired && !value ? 'in' : ''}viableField`](this.fieldName)
    }
  }
}
